import { EdgelessCRUDIdentifier } from '@blocksuite/affine-block-surface';
import { ConnectorPathGenerator } from '@blocksuite/affine-gfx-connector/connector-manager';
import {
  ConnectorElementModel,
  type RootBlockModel,
} from '@blocksuite/affine-model';
import {
  agentFollowing$,
  renderCachedUserAvatarUrl,
  revokeCachedUserAvatarObjectUrl,
  UserAvatarCacheProvider,
} from '@blocksuite/affine-shared/services';
import {
  agentPointerViewElement,
  isRenderableRemotePresenceAvatarUrl,
  isStreamLainMotionEnabled,
  requestThrottledConnectedFrame,
  stepRemotePresenceCursorPosition,
} from '@blocksuite/affine-shared/utils';
import {
  Bound,
  isSerializedXYWH,
  type SerializedXYWH,
} from '@blocksuite/global/gfx';
import { MultiCursorDuotoneIcon } from '@blocksuite/icons/lit';
import { WidgetComponent } from '@blocksuite/std';
import {
  getActiveRemoteCanvasDragPreviewEntries,
  GfxBlockElementModel,
  GfxControllerIdentifier,
  type GfxModel,
  GfxPrimitiveElementModel,
  resolveCanvasDragPreviewXYWH,
} from '@blocksuite/std/gfx';
import {
  type AgentPointer,
  assistantPresenceKey,
  type CanvasDragPreviewState,
  isAssistantPresenceKey,
  presenceOwnerClientId,
  readPresenceUser,
  type UserInfo,
} from '@blocksuite/store';
import { effect } from '@preact/signals-core';
import { css, html, nothing, render as renderLit } from 'lit';
import { state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { tap } from 'rxjs';

import { AgentDatabaseReveal } from '../agent-database-reveal';
import {
  AgentPointerDisplay,
  createAgentPointerOwnerResolver,
} from '../agent-pointer-display';
import { RemoteColorManager } from '../manager/remote-color-manager';
import { AgentCanvasReveal } from './agent-canvas-reveal';
import {
  applyVisibleRemoteBlockSelectionDecoration,
  clearRemoteBlockSelectionDecoration,
  initializeRemoteBlockSelectionDisplay,
  type RemoteBlockSelectionPresenceAvatar,
  resolveRemoteBlockSelectionLayerBinding,
} from './block-selection-decoration';
import { resolveRemoteSelectionPresentation } from './selection-presentation';

export {
  applyRemoteBlockSelectionDecoration,
  applyVisibleRemoteBlockSelectionDecoration,
  clearRemoteBlockSelectionDecoration,
  resolveRemoteBlockSelectionLayerBinding,
} from './block-selection-decoration';
export { resolveRemoteSelectionPresentation } from './selection-presentation';

export const AFFINE_EDGELESS_REMOTE_SELECTION_WIDGET =
  'affine-edgeless-remote-selection-widget';

/**
 * Presentation state from Awareness
 * In 'frames' mode, follower cursors are automatically hidden
 */
interface PresentationAwarenessState {
  isPresenting: boolean;
  mode: 'free' | 'frames';
}

interface RemotePresenceAvatar {
  color: string;
  id: number;
  title: string;
  user?: UserInfo;
  x: number;
  y: number;
}

interface RemoteBlockPresenceAvatar extends Omit<
  RemoteBlockSelectionPresenceAvatar,
  'right' | 'top'
> {
  id: number;
  title: string;
  user?: UserInfo;
}

const REMOTE_SELECTION_AVATAR_SIZE = 36;
const REMOTE_SELECTION_AVATAR_OVERLAP = 9;
const REMOTE_SELECTION_AVATAR_STACK_OFFSET = 30;
const REMOTE_DRAG_PREVIEW_SETTLE_HOLD_MS = 2_000;
const REMOTE_DRAG_PREVIEW_SYNC_EPSILON = 0.5;
const REMOTE_BLOCK_SELECTION_BINDING_RETRY_FRAMES = 120;

function areSerializedXYWHClose(
  actual: SerializedXYWH,
  expected: SerializedXYWH
): boolean {
  if (!isSerializedXYWH(actual) || !isSerializedXYWH(expected)) {
    return actual === expected;
  }

  const actualBound = Bound.deserialize(actual);
  const expectedBound = Bound.deserialize(expected);
  return (
    Math.abs(actualBound.x - expectedBound.x) <=
      REMOTE_DRAG_PREVIEW_SYNC_EPSILON &&
    Math.abs(actualBound.y - expectedBound.y) <=
      REMOTE_DRAG_PREVIEW_SYNC_EPSILON &&
    Math.abs(actualBound.w - expectedBound.w) <=
      REMOTE_DRAG_PREVIEW_SYNC_EPSILON &&
    Math.abs(actualBound.h - expectedBound.h) <=
      REMOTE_DRAG_PREVIEW_SYNC_EPSILON
  );
}

function areNumbersClose(actual: number, expected: number): boolean {
  return Math.abs(actual - expected) <= REMOTE_DRAG_PREVIEW_SYNC_EPSILON;
}

function getPersistedRotate(model: GfxModel): number {
  if (model instanceof GfxBlockElementModel && 'rotate' in model.props) {
    return model.props.rotate as number;
  }

  if (model instanceof GfxPrimitiveElementModel) {
    return model.persistedRotate;
  }

  return model.rotate;
}

function isTransientPreviewSynced(model: GfxModel): boolean {
  if (
    model instanceof GfxBlockElementModel ||
    model instanceof GfxPrimitiveElementModel
  ) {
    const xywhSynced =
      !model.transientXYWH ||
      areSerializedXYWHClose(model.xywh, model.transientXYWH);
    const rotateSynced =
      !(
        model instanceof GfxBlockElementModel ||
        model instanceof GfxPrimitiveElementModel
      ) ||
      model.transientRotate === undefined ||
      areNumbersClose(getPersistedRotate(model), model.transientRotate);

    return xywhSynced && rotateSynced;
  }

  return true;
}

function getRemoteUserInitials(user: UserInfo | undefined): string {
  const name = user?.name?.trim();
  if (!name) return '?';

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');

  return initials || name[0]?.toUpperCase() || '?';
}

export class EdgelessRemoteSelectionWidget extends WidgetComponent<RootBlockModel> {
  private _agentPointerDisplay: AgentPointerDisplay | null = null;
  private _agentCanvasReveal: AgentCanvasReveal | null = null;

  @state()
  private accessor _agentGhosts: readonly {
    id: string;
    xywh: SerializedXYWH;
    ownerUserId: string;
  }[] = [];

  static override styles = css`
    :host {
      pointer-events: none;
      position: absolute;
      left: 0;
      top: 0;
      transform-origin: left top;
      contain: size layout;
      z-index: 1;
    }

    .remote-rect {
      position: absolute;
      top: 0;
      left: 0;
      border-radius: 4px;
      box-sizing: border-box;
      border-width: 2px;
      z-index: 1;
      transform-origin: center center;
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.72);
    }

    .remote-selection-avatar-anchor {
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      transform-origin: left top;
      z-index: 2;
    }

    .remote-selection-avatar {
      position: absolute;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      overflow: visible;
      border-radius: 50%;
      color: white;
      font-size: 16px;
      font-weight: 600;
      line-height: 1;
      text-transform: uppercase;
    }

    .remote-selection-avatar::before {
      content: '';
      position: absolute;
      inset: -2px;
      border: 2px solid white;
      border-radius: inherit;
      box-sizing: border-box;
      pointer-events: none;
      box-shadow:
        0 1px 6px rgba(0, 0, 0, 0.2),
        0 0 0 1px rgba(0, 0, 0, 0.08);
    }

    .remote-selection-avatar-content {
      position: relative;
      width: 100%;
      height: 100%;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: inherit;
    }

    .remote-selection-avatar-content > img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
    }

    .remote-cursor {
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: left top;
      z-index: 1;
      /* sl-dda7: GPU-accelerated for LERP animation */
      will-change: transform;
    }

    .remote-cursor > svg {
      display: block;
    }

    .remote-username {
      margin-left: 22px;
      margin-top: -2px;

      color: white;

      max-width: 160px;
      padding: 0px 3px;
      border: 1px solid var(--streamlain-v2-layer-blackAlpha-10);

      box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.16);
      border-radius: 4px;

      font-size: 12px;
      line-height: 18px;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `;

  /**
   * sl-dda7: LERP (Linear Interpolation) for smooth remote cursor movement.
   * Compensates for network/coalescing latency.
   * Time-based smoothing keeps motion stable across 60Hz/120Hz displays and
   * avoids a new hard ease-out curve on every awareness packet.
   */
  private _remoteColorManager: RemoteColorManager | null = null;

  /** Target positions from awareness (raw, uninterpolated) */
  private _remoteCursorTargets: Map<
    number,
    { x: number; y: number; user?: UserInfo }
  > = new Map();

  /** Displayed positions (interpolated) — stored mutably for rAF perf */
  private readonly _displayedPositions: Map<number, { x: number; y: number }> =
    new Map();

  private readonly _remoteDragPreviewElementIds = new Set<string>();

  private readonly _remoteDragPreviewMoveStates = new Map<
    number,
    {
      dragId: string;
      x: number;
      y: number;
      preview: CanvasDragPreviewState;
      basis: { x: number; y: number };
      elements: { elementId: string; bound: Bound; rotate?: number }[];
    }
  >();

  private _remoteDragPreviewConnectorsDirty = false;
  private _applyingRemoteDragPreviews = false;

  private readonly _remoteDragPreviewConnectorIds = new Set<string>();

  private _remoteDragPreviewConnectorSignature = '';

  private readonly _remoteDragPreviewHoldDeadlines = new Map<string, number>();

  private readonly _remoteDragPreviewHoldTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private _lerpAnimationId: number | null = null;
  private _lastLerpTimestamp: number | null = null;

  private readonly _updateOnElementChange = (
    element: string | { id: string }
  ) => {
    const id = typeof element === 'string' ? element : element.id;
    this._agentPointerDisplay?.update();
    this._remoteDragPreviewConnectorsDirty = true;
    // Transient element notifications remain available to renderers and hit
    // testing; our own selection/connector pass runs after all members moved.
    if (this._applyingRemoteDragPreviews) return;

    this._releaseRemoteDragPreviewElementIfSynced(id);
    if (this.isConnected && this.selection.hasRemote(id)) {
      this._remoteBlockSelectionBindingRetryCount = 0;
      this._updateRemoteRects();
    }
  };

  private readonly _retryRemoteBlockSelectionBinding =
    requestThrottledConnectedFrame(() => {
      if (!this.isConnected) return;
      this._updateRemoteRects();
    }, this);

  private readonly _updateRemoteCursor = () => {
    const status = this.store.awarenessStore.getStates();

    const targets: EdgelessRemoteSelectionWidget['_remoteCursorTargets'] =
      new Map();

    this.selection.remoteCursorSelectionMap.forEach(
      (cursorSelection, clientId) => {
        targets.set(clientId, {
          x: cursorSelection.x,
          y: cursorSelection.y,
          user: readPresenceUser(status, clientId),
        });
      }
    );

    // Initialize displayed positions for newly appeared cursors
    for (const [id, target] of targets) {
      if (!this._displayedPositions.has(id)) {
        this._displayedPositions.set(id, { x: target.x, y: target.y });
      }
    }

    // Remove cursors that left
    for (const id of this._displayedPositions.keys()) {
      if (!targets.has(id)) {
        this._displayedPositions.delete(id);
      }
    }

    this._remoteCursorTargets = targets;
    this._startLerpLoop();
  };

  private readonly _lerpLoop = (timestamp: number) => {
    const elapsed =
      this._lastLerpTimestamp === null
        ? 16
        : timestamp - this._lastLerpTimestamp;
    this._lastLerpTimestamp = timestamp;

    const targets = new Map(this._remoteCursorTargets);
    const displayed = this._displayedPositions;
    // Cursor and geometry consume the same drag packet. Independent cursor
    // presence can arrive in another batch, so it is not this frame's anchor.
    for (const [clientId, state] of getActiveRemoteCanvasDragPreviewEntries({
      localClientId: this.store.awarenessStore.awareness.clientID,
      now: Date.now(),
      states: this.store.awarenessStore.getStates(),
    })) {
      const preview = state.canvasDragPreview;
      const cursor = preview?.cursor;
      if (
        preview?.operation !== 'move' ||
        preview.status !== 'dragging' ||
        !cursor ||
        !Number.isFinite(cursor.x) ||
        !Number.isFinite(cursor.y)
      )
        continue;
      targets.set(clientId, { x: cursor.x, y: cursor.y, user: state.user });
      if (!displayed.has(clientId)) displayed.set(clientId, { ...cursor });
    }
    let anyMoving = false;

    const remoteCursors: EdgelessRemoteSelectionWidget['_remoteCursors'] =
      new Map();

    for (const [id, target] of targets) {
      const pos = displayed.get(id);
      if (!pos) continue;

      const stepped = stepRemotePresenceCursorPosition({
        current: pos,
        elapsedMs: elapsed,
        target,
      });
      pos.x = stepped.next.x;
      pos.y = stepped.next.y;
      anyMoving ||= stepped.moving;

      remoteCursors.set(id, { x: pos.x, y: pos.y, user: target.user });
    }

    this._remoteCursors = remoteCursors;
    anyMoving = this._applyRemoteDragPreviews(elapsed) || anyMoving;

    if (anyMoving) {
      this._lerpAnimationId = requestAnimationFrame(this._lerpLoop);
    } else {
      this._lerpAnimationId = null;
      this._lastLerpTimestamp = null;
    }
  };

  private _startLerpLoop(): void {
    if (this._lerpAnimationId !== null) return;
    this._lerpAnimationId = requestAnimationFrame(this._lerpLoop);
  }

  private _stopLerpLoop(): void {
    if (this._lerpAnimationId !== null) {
      cancelAnimationFrame(this._lerpAnimationId);
      this._lerpAnimationId = null;
    }
    this._lastLerpTimestamp = null;
  }

  private readonly _updateRemoteRects = () => {
    const { selection } = this;
    const status = this.store.awarenessStore.getStates();
    const remoteSelectionsMap = selection.remoteSurfaceSelectionsMap;
    const visibleClientIds = this.getVisibleClientIds();
    const remoteRects: EdgelessRemoteSelectionWidget['_remoteRects'] =
      new Map();
    const nextRemoteBlockSelectionViews = new Set<HTMLElement>();
    const nextRemoteBlockSelectionStates = new Map<
      HTMLElement,
      { avatars: RemoteBlockPresenceAvatar[]; rotate: number }
    >();

    remoteSelectionsMap.forEach((selections, clientId) => {
      const elementIds = new Set(
        selections.flatMap(selection => selection.elements)
      );
      const elements = [...elementIds]
        .map(id => this.crud.getElementById(id))
        .filter(element => element) as GfxModel[];

      if (elements.length === 0) return;

      // Remote presence is block-level, but its visual geometry must use the
      // same raw block rect + rotation contract as the local selection widget.
      // Using elementBound here turns a rotated Polaroid into an already
      // rotated axis-aligned box and then produces a visibly oversized frame.
      const { rect, rotate } = resolveRemoteSelectionPresentation(elements);
      if (rect.width === 0 || rect.height === 0) return;

      const singleBlockSelection =
        elements.length === 1 && elements[0] instanceof GfxBlockElementModel;
      const binding = resolveRemoteBlockSelectionLayerBinding(
        singleBlockSelection,
        singleBlockSelection ? this.gfx.view.get(elements[0]) : null
      );
      if (binding.blockView) {
        const color = this._remoteColorManager?.get(clientId) ?? '#1e88e5';
        const state = nextRemoteBlockSelectionStates.get(binding.blockView) ?? {
          avatars: [],
          rotate,
        };
        state.avatars.push({
          clientId,
          color,
          id: clientId,
          title: readPresenceUser(status, clientId)?.name ?? 'Unknown',
          user: readPresenceUser(status, clientId),
        });
        nextRemoteBlockSelectionStates.set(binding.blockView, state);
      }

      remoteRects.set(clientId, {
        width: rect.width,
        height: rect.height,
        borderStyle: 'solid',
        left: rect.left,
        top: rect.top,
        rotate,
        awaitsBlockStackingContext: binding.awaitsBlockView,
        usesBlockStackingContext: !binding.renderGlobalRect,
        user: readPresenceUser(status, clientId),
      });
    });

    for (const [view, state] of nextRemoteBlockSelectionStates) {
      const { avatarTargets, visibleAvatars } =
        applyVisibleRemoteBlockSelectionDecoration(
          view,
          state.rotate,
          state.avatars,
          visibleClientIds,
          REMOTE_SELECTION_AVATAR_SIZE,
          REMOTE_SELECTION_AVATAR_STACK_OFFSET
        );
      visibleAvatars.forEach(avatar => {
        const target = avatarTargets.get(avatar.clientId);
        if (target) {
          this.renderRemoteBlockPresenceAvatar(target, avatar, state.rotate);
        }
      });
      if (visibleAvatars.length > 0) {
        nextRemoteBlockSelectionViews.add(view);
      }
    }

    for (const view of this._remoteBlockSelectionViews) {
      if (!nextRemoteBlockSelectionViews.has(view)) {
        clearRemoteBlockSelectionDecoration(view);
      }
    }
    this._remoteBlockSelectionViews.clear();
    for (const view of nextRemoteBlockSelectionViews) {
      this._remoteBlockSelectionViews.add(view);
    }
    this._remoteRects = remoteRects;
    const awaitsBlockStackingContext = [...remoteRects.values()].some(
      rect => rect.awaitsBlockStackingContext
    );
    if (
      awaitsBlockStackingContext &&
      this._remoteBlockSelectionBindingRetryCount <
        REMOTE_BLOCK_SELECTION_BINDING_RETRY_FRAMES
    ) {
      this._remoteBlockSelectionBindingRetryCount += 1;
      this._retryRemoteBlockSelectionBinding();
    } else if (!awaitsBlockStackingContext) {
      this._remoteBlockSelectionBindingRetryCount = 0;
    }
  };

  private _clearRemoteBlockSelectionDecorations(): void {
    for (const view of this._remoteBlockSelectionViews) {
      clearRemoteBlockSelectionDecoration(view);
    }
    this._remoteBlockSelectionViews.clear();
  }

  private _placeTransientElement(
    model: GfxModel,
    xywh: SerializedXYWH | undefined
  ): void {
    model.transientXYWH = xywh;
    this.gfx.grid.update(model);
    if (model instanceof GfxBlockElementModel) {
      const view = this.gfx.view.get(model) as {
        refreshGfxTransform?: () => void;
        requestUpdate?: () => void;
      } | null;
      view?.refreshGfxTransform?.();
      view?.requestUpdate?.();
    }
    this.selection.slots.transientGeometryUpdated.next(model.id);
  }

  private _applyRemoteDragPreviewElement(
    model: GfxModel,
    element: { rotate?: number; xywh: SerializedXYWH }
  ) {
    this._cancelRemoteDragPreviewHold(model.id);
    this._agentCanvasReveal?.interrupt(model.id);
    if (
      model.transientXYWH === element.xywh &&
      model.transientRotate === element.rotate
    )
      return false;
    model.transientRotate = element.rotate;
    this._placeTransientElement(model, element.xywh);
    return true;
  }

  private _canUpdateRemoteDragPreviewConnector(
    connector: ConnectorElementModel
  ): boolean {
    const sourceOk =
      (connector.source?.id &&
        !!this.crud.getElementById(connector.source.id)) ||
      (!connector.source?.id && !!connector.source?.position);
    const targetOk =
      (connector.target?.id &&
        !!this.crud.getElementById(connector.target.id)) ||
      (!connector.target?.id && !!connector.target?.position);

    return !!sourceOk && !!targetOk;
  }

  private _getRemoteDragPreviewConnectors(elementIds: Iterable<string>) {
    const surface = this.surface as {
      getConnectors?: (id: string) => ConnectorElementModel[];
    } | null;
    const connectors = new Map<string, ConnectorElementModel>();

    if (!surface?.getConnectors) {
      return connectors;
    }

    for (const elementId of elementIds) {
      surface.getConnectors(elementId).forEach(connector => {
        connectors.set(connector.id, connector);
      });
    }

    return connectors;
  }

  private _getRemoteDragPreviewConnectorSignature(
    endpointIds: ReadonlySet<string>,
    connectorIds: Iterable<string>
  ): string {
    const endpointSignature = [...endpointIds]
      .sort()
      .map(elementId => {
        const model = this.crud.getElementById(elementId);
        if (
          model instanceof GfxBlockElementModel ||
          model instanceof GfxPrimitiveElementModel
        ) {
          return [
            elementId,
            model.xywh,
            model.transientXYWH ?? '',
            model.rotate,
            model.transientRotate ?? '',
          ].join(':');
        }
        return `${elementId}:missing`;
      })
      .join('|');
    const connectorSignature = [...connectorIds].sort().join(',');

    return `${endpointSignature}::${connectorSignature}`;
  }

  private _updateRemoteDragPreviewConnector(connector: ConnectorElementModel) {
    if (!this._canUpdateRemoteDragPreviewConnector(connector)) {
      return;
    }

    try {
      ConnectorPathGenerator.updatePath(
        connector,
        null,
        id => this.crud.getElementById(id) as GfxModel | null,
        { updateLabel: false }
      );
      this.gfx.grid.update(connector);
      this.selection.slots.transientGeometryUpdated.next(connector.id);
    } catch (error) {
      console.error(
        `[remoteDragPreview] update connector path failed for ${connector.id}:`,
        error
      );
    }
  }

  private _refreshRemoteDragPreviewConnectors() {
    const endpointIds = new Set(this._remoteDragPreviewElementIds);
    this._remoteDragPreviewHoldDeadlines.forEach((_, elementId) => {
      endpointIds.add(elementId);
    });

    const nextConnectors = this._getRemoteDragPreviewConnectors(endpointIds);
    const signature = this._getRemoteDragPreviewConnectorSignature(
      endpointIds,
      nextConnectors.keys()
    );
    if (signature === this._remoteDragPreviewConnectorSignature) {
      return;
    }
    this._remoteDragPreviewConnectorSignature = signature;

    nextConnectors.forEach(connector => {
      this._updateRemoteDragPreviewConnector(connector);
    });

    this._remoteDragPreviewConnectorIds.forEach(connectorId => {
      if (nextConnectors.has(connectorId)) {
        return;
      }
      const connector = this.crud.getElementById(connectorId);
      if (connector instanceof ConnectorElementModel) {
        this._updateRemoteDragPreviewConnector(connector);
      }
    });

    this._remoteDragPreviewConnectorIds.clear();
    nextConnectors.forEach((_, connectorId) => {
      this._remoteDragPreviewConnectorIds.add(connectorId);
    });
  }

  private _applyRemoteDragPreviews(elapsedMs = 16) {
    this._applyingRemoteDragPreviews = true;
    try {
      const now = Date.now();
      let changed = false;
      let anyMoving = false;
      const moveClientIds = new Set<number>();
      const nextElementIds = new Set<string>();
      const states = this.store.awarenessStore.getStates();
      const entries = getActiveRemoteCanvasDragPreviewEntries({
        localClientId: this.store.awarenessStore.awareness.clientID,
        now,
        states,
      });

      for (const [clientId, state] of entries) {
        const preview = state.canvasDragPreview;
        if (!preview) {
          continue;
        }

        if (
          preview.operation === 'move' &&
          preview.status === 'dragging' &&
          preview.translation
        ) {
          const target = preview.translation;
          if (!Number.isFinite(target.x) || !Number.isFinite(target.y))
            continue;
          moveClientIds.add(clientId);
          let move = this._remoteDragPreviewMoveStates.get(clientId);
          const basis = preview.basisTranslation ?? target;
          const fresh = !move || move.dragId !== preview.dragId;
          const snapshotChanged =
            fresh ||
            (preview.basisTranslation
              ? move?.preview.basisVersion !== preview.basisVersion ||
                move?.basis.x !== basis.x ||
                move?.basis.y !== basis.y
              : move?.preview.elements !== preview.elements);
          if (fresh) {
            move = {
              dragId: preview.dragId,
              x: target.x,
              y: target.y,
              preview,
              basis,
              elements: [],
            };
            this._remoteDragPreviewMoveStates.set(clientId, move);
          }
          if (!move) continue;
          if (snapshotChanged) {
            // Absolute bounds keep existing receivers working. Remove the
            // shared target offset once per packet to recover start positions
            // with the latest live dimensions, then reuse them across frames.
            move.basis = basis;
            move.elements = preview.elements.flatMap(element => {
              const xywh = resolveCanvasDragPreviewXYWH({ element });
              return xywh
                ? [
                    {
                      elementId: element.elementId,
                      bound: Bound.deserialize(xywh).moveDelta(
                        -basis.x,
                        -basis.y
                      ),
                      rotate: element.rotate,
                    },
                  ]
                : [];
            });
          }
          move.preview = preview;
          const displayed = this._displayedPositions?.get(clientId);
          const cursor = preview.cursor;
          const stepped =
            cursor &&
            displayed &&
            Number.isFinite(cursor.x) &&
            Number.isFinite(cursor.y)
              ? {
                  next: {
                    x: target.x + displayed.x - cursor.x,
                    y: target.y + displayed.y - cursor.y,
                  },
                  moving: displayed.x !== cursor.x || displayed.y !== cursor.y,
                }
              : stepRemotePresenceCursorPosition({
                  current: move,
                  target,
                  elapsedMs,
                });
          const translated =
            fresh || stepped.next.x !== move.x || stepped.next.y !== move.y;
          move.x = stepped.next.x;
          move.y = stepped.next.y;
          anyMoving ||= stepped.moving;
          for (const element of move.elements) {
            const model = this.crud.getElementById(element.elementId);
            if (!model) continue;
            nextElementIds.add(element.elementId);
            if (
              !translated &&
              !snapshotChanged &&
              !this._remoteDragPreviewConnectorsDirty &&
              this._remoteDragPreviewElementIds.has(element.elementId)
            )
              continue;
            changed =
              this._applyRemoteDragPreviewElement(model, {
                rotate: element.rotate,
                xywh: element.bound.moveDelta(move.x, move.y).serialize(),
              }) || changed;
          }
          continue;
        }

        for (const element of preview.elements) {
          const model = this.crud.getElementById(element.elementId);
          if (!model) {
            continue;
          }
          const xywh = resolveCanvasDragPreviewXYWH({
            element,
          });
          if (!xywh) {
            continue;
          }
          nextElementIds.add(element.elementId);
          changed =
            this._applyRemoteDragPreviewElement(model, {
              rotate: element.rotate,
              xywh,
            }) || changed;
        }
      }

      this._remoteDragPreviewMoveStates.forEach((_, clientId) => {
        if (!moveClientIds.has(clientId))
          this._remoteDragPreviewMoveStates.delete(clientId);
      });
      const membershipChanged =
        nextElementIds.size !== this._remoteDragPreviewElementIds.size ||
        [...nextElementIds].some(
          id => !this._remoteDragPreviewElementIds.has(id)
        );
      this._remoteDragPreviewElementIds.forEach(elementId => {
        if (nextElementIds.has(elementId)) {
          return;
        }
        this._releaseRemoteDragPreviewElement(elementId);
      });
      this._remoteDragPreviewElementIds.clear();
      nextElementIds.forEach(elementId =>
        this._remoteDragPreviewElementIds.add(elementId)
      );
      if (
        changed ||
        membershipChanged ||
        this._remoteDragPreviewConnectorsDirty
      ) {
        this._remoteDragPreviewConnectorsDirty = false;
        this._refreshRemoteDragPreviewConnectors();
      }
      if (changed || membershipChanged) {
        this._updateRemoteRects();
      }
      return anyMoving;
    } finally {
      this._applyingRemoteDragPreviews = false;
    }
  }

  private _cancelRemoteDragPreviewHold(elementId: string) {
    this._remoteDragPreviewHoldDeadlines.delete(elementId);
    const timer = this._remoteDragPreviewHoldTimers.get(elementId);
    if (timer) {
      clearTimeout(timer);
      this._remoteDragPreviewHoldTimers.delete(elementId);
    }
  }

  private _releaseRemoteDragPreviewElement(
    elementId: string,
    now = Date.now()
  ) {
    const model = this.crud.getElementById(elementId);
    if (
      !model ||
      (!(model instanceof GfxBlockElementModel) &&
        !(model instanceof GfxPrimitiveElementModel)) ||
      isTransientPreviewSynced(model)
    ) {
      this._clearRemoteDragPreviewElement(elementId);
      return;
    }

    const deadline =
      this._remoteDragPreviewHoldDeadlines.get(elementId) ??
      now + REMOTE_DRAG_PREVIEW_SETTLE_HOLD_MS;
    this._remoteDragPreviewHoldDeadlines.set(elementId, deadline);
    if (now >= deadline) {
      this._clearRemoteDragPreviewElement(elementId);
      return;
    }

    if (!this._remoteDragPreviewHoldTimers.has(elementId)) {
      this._remoteDragPreviewHoldTimers.set(
        elementId,
        setTimeout(() => {
          this._remoteDragPreviewHoldTimers.delete(elementId);
          this._releaseRemoteDragPreviewElement(elementId);
        }, deadline - now)
      );
    }
  }

  private _releaseRemoteDragPreviewElementIfSynced(elementId: string) {
    if (!this._remoteDragPreviewHoldDeadlines.has(elementId)) {
      return;
    }

    const model = this.crud.getElementById(elementId);
    if (
      !model ||
      (!(model instanceof GfxBlockElementModel) &&
        !(model instanceof GfxPrimitiveElementModel)) ||
      isTransientPreviewSynced(model)
    ) {
      this._clearRemoteDragPreviewElement(elementId);
    }
  }

  private _clearRemoteDragPreviewElement(
    elementId: string,
    refreshConnectors = true
  ) {
    this._cancelRemoteDragPreviewHold(elementId);
    const model = this.crud.getElementById(elementId);
    if (!model) {
      if (refreshConnectors) {
        this._refreshRemoteDragPreviewConnectors();
      }
      return;
    }

    model.transientRotate = undefined;
    this._placeTransientElement(model, undefined);
    if (refreshConnectors) this._refreshRemoteDragPreviewConnectors();
  }

  private _clearRemoteDragPreviews() {
    this._remoteDragPreviewMoveStates.clear();
    const elementIds = new Set(this._remoteDragPreviewElementIds);
    this._remoteDragPreviewHoldDeadlines.forEach((_, elementId) => {
      elementIds.add(elementId);
    });
    elementIds.forEach(elementId =>
      this._clearRemoteDragPreviewElement(elementId, false)
    );
    this._remoteDragPreviewElementIds.clear();
    this._remoteDragPreviewHoldTimers.forEach(timer => clearTimeout(timer));
    this._remoteDragPreviewHoldTimers.clear();
    this._remoteDragPreviewHoldDeadlines.clear();
    this._refreshRemoteDragPreviewConnectors();
    this._remoteDragPreviewConnectorIds.clear();
  }

  private getFullscreenPresenceScope() {
    return `database:${this.store.id}:fullscreen`;
  }

  private readonly _updateTransform = requestThrottledConnectedFrame(() => {
    if (
      document.documentElement.dataset
        .streamlainPerformanceSuspendRemoteSelectionViewport === 'true'
    ) {
      return;
    }
    const { translateX, translateY, zoom } = this.gfx.viewport;

    this.style.setProperty('--v-zoom', `${zoom}`);

    this.style.setProperty(
      'transform',
      `translate(${translateX}px, ${translateY}px) scale(var(--v-zoom))`
    );
  }, this);

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get crud() {
    return this.std.get(EdgelessCRUDIdentifier);
  }

  get selection() {
    return this.gfx.selection;
  }

  get surface() {
    return this.gfx.surface;
  }

  /**
   * Get visible client IDs based on presentation state
   *
   * Returns null if all cursors should be visible (normal mode or free mode).
   * Returns Set of client IDs if filtering is needed (frame mode presentation).
   *
   * In 'frames' mode: Only presenter's cursor is visible to followers.
   * In 'free' mode: All cursors are visible.
   */
  private getVisibleClientIds(): Set<number> | null {
    const awareness = this.store.awarenessStore.awareness;
    const states = awareness.getStates();
    const myClientId = awareness.clientID;

    // Find presenter(s) in awareness
    let presenterClientId: number | null = null;
    let presenterMode: 'free' | 'frames' = 'free';

    for (const [clientId, state] of states) {
      const typedState = state as {
        presentation?: PresentationAwarenessState;
      };

      if (typedState.presentation?.isPresenting) {
        presenterClientId = clientId;
        presenterMode = typedState.presentation.mode ?? 'free';
        break; // Only support single presenter for now
      }
    }

    // No presenter = normal mode, show all
    if (presenterClientId === null) {
      return null;
    }

    // I am the presenter = show all (presenter sees all follower cursors)
    if (presenterClientId === myClientId) {
      return null;
    }

    // Free mode = show all cursors
    if (presenterMode === 'free') {
      return null;
    }

    // Frame mode = only show the presenter and their assistant (followers hidden)
    return new Set([
      presenterClientId,
      assistantPresenceKey(presenterClientId),
    ]);
  }

  private getVisibleClientIdsSignature(): string {
    const visibleClientIds = this.getVisibleClientIds();
    if (visibleClientIds === null) return 'all';
    return [...visibleClientIds].sort((a, b) => a - b).join(',');
  }

  private getRemoteAwarenessDisplaySignature(): string {
    const status = this.store.awarenessStore.getStates();
    const clientIds = new Set<number>();

    this.selection.remoteCursorSelectionMap.forEach((_, id) =>
      clientIds.add(id)
    );
    this.selection.remoteSurfaceSelectionsMap.forEach((_, id) =>
      clientIds.add(id)
    );
    status.forEach((state, id) => {
      if (state.canvasDragPreview) {
        clientIds.add(id);
      }
      if (
        state.databaseFullscreenPresence?.scope ===
        this.getFullscreenPresenceScope()
      ) {
        clientIds.add(id);
      }
    });

    return [...clientIds]
      .sort((a, b) => a - b)
      .map(id => {
        // A person, or the assistant shown in its owner's colour.
        const state = status.get(presenceOwnerClientId(id));
        const user = readPresenceUser(status, id);
        const fullscreenPresence = state?.databaseFullscreenPresence;
        const dragPreview = state?.canvasDragPreview;
        return [
          id,
          state?.color ?? '',
          user?.id ?? '',
          user?.name ?? '',
          user?.avatar ?? '',
          fullscreenPresence?.scope ?? '',
          fullscreenPresence?.databaseId ?? '',
          fullscreenPresence?.updatedAt ?? '',
          dragPreview?.dragId ?? '',
          dragPreview?.operation ?? '',
          dragPreview?.status ?? '',
          dragPreview?.updatedAt ?? '',
          dragPreview?.elementIds.join(',') ?? '',
        ].join(':');
      })
      .join('|');
  }

  private getFullscreenPresenceAvatars(
    avatarStackCounts: Map<string, number>,
    excludedClientIds: ReadonlySet<number>,
    shouldShowCursor: (clientId: number) => boolean
  ): RemotePresenceAvatar[] {
    const status = this.store.awarenessStore.getStates();
    const scope = this.getFullscreenPresenceScope();
    const avatars: RemotePresenceAvatar[] = [];

    status.forEach((state, id) => {
      if (id === this.store.awarenessStore.awareness.clientID) return;
      if (excludedClientIds.has(id) || !shouldShowCursor(id)) return;

      const presence = state.databaseFullscreenPresence;
      if (!presence || presence.scope !== scope) return;

      const element = this.crud.getElementById(presence.databaseId);
      if (!element) return;

      const { rect } = resolveRemoteSelectionPresentation([
        element as GfxModel,
      ]);
      if (rect.width === 0 || rect.height === 0) return;

      const avatarKey = [
        Math.round(rect.left),
        Math.round(rect.top),
        Math.round(rect.width),
        Math.round(rect.height),
      ].join(':');
      const avatarIndex = avatarStackCounts.get(avatarKey) ?? 0;
      avatarStackCounts.set(avatarKey, avatarIndex + 1);
      avatars.push({
        id,
        x:
          rect.left +
          rect.width -
          REMOTE_SELECTION_AVATAR_SIZE +
          REMOTE_SELECTION_AVATAR_OVERLAP -
          avatarIndex * REMOTE_SELECTION_AVATAR_STACK_OFFSET,
        y:
          rect.top -
          REMOTE_SELECTION_AVATAR_SIZE +
          REMOTE_SELECTION_AVATAR_OVERLAP,
        user: state.user,
        color: this._remoteColorManager?.get(id) ?? '#1e88e5',
        title: state.user?.name
          ? `${state.user.name} is in fullscreen`
          : 'In fullscreen',
      });
    });

    return avatars.sort((a, b) => a.id - b.id);
  }

  private renderRemotePresenceAvatarVisual({
    color,
    title,
    user,
    inline = false,
    counterRotate = 0,
  }: Pick<RemotePresenceAvatar, 'color' | 'title' | 'user'> & {
    inline?: boolean;
    counterRotate?: number;
  }) {
    const rawAvatarUrl = user?.avatar;
    const avatarUrl = isRenderableRemotePresenceAvatarUrl(rawAvatarUrl)
      ? rawAvatarUrl
      : undefined;
    const avatarCache = this.std.getOptional(UserAvatarCacheProvider);
    const avatar =
      avatarUrl && user?.id
        ? renderCachedUserAvatarUrl({
            avatarCache,
            avatarUrl,
            userId: user.id,
            render: (url, cached) =>
              html`<img
                src=${url}
                alt=${user?.name ?? ''}
                style=${inline
                  ? styleMap({
                      display: 'block',
                      height: '100%',
                      objectFit: 'cover',
                      width: '100%',
                    })
                  : nothing}
                @load=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
                @error=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
              />`,
          })
        : null;

    return html`<div
      class="remote-selection-avatar"
      title=${title}
      style=${inline
        ? styleMap({
            alignItems: 'center',
            borderRadius: '50%',
            boxSizing: 'border-box',
            color: 'white',
            display: 'flex',
            fontSize: '16px',
            fontWeight: '600',
            height: `${REMOTE_SELECTION_AVATAR_SIZE}px`,
            justifyContent: 'center',
            lineHeight: '1',
            overflow: 'visible',
            position: 'absolute',
            textTransform: 'uppercase',
            transform:
              counterRotate === 0 ? undefined : `rotate(${-counterRotate}deg)`,
            transformOrigin: 'center center',
            width: `${REMOTE_SELECTION_AVATAR_SIZE}px`,
          })
        : nothing}
    >
      ${inline
        ? html`<div
            style=${styleMap({
              border: '2px solid white',
              borderRadius: 'inherit',
              boxShadow:
                '0 1px 6px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.08)',
              boxSizing: 'border-box',
              inset: '-2px',
              pointerEvents: 'none',
              position: 'absolute',
            })}
          ></div>`
        : nothing}
      <div
        class="remote-selection-avatar-content"
        style=${styleMap({
          alignItems: inline ? 'center' : undefined,
          backgroundColor: color,
          borderRadius: inline ? 'inherit' : undefined,
          display: inline ? 'flex' : undefined,
          height: inline ? '100%' : undefined,
          justifyContent: inline ? 'center' : undefined,
          overflow: inline ? 'hidden' : undefined,
          position: inline ? 'relative' : undefined,
          width: inline ? '100%' : undefined,
          zIndex: inline ? '1' : undefined,
        })}
      >
        ${avatar ?? html`<span>${getRemoteUserInitials(user)}</span>`}
      </div>
    </div>`;
  }

  private renderRemotePresenceAvatar({
    color,
    id,
    title,
    user,
    x,
    y,
  }: RemotePresenceAvatar) {
    return html`<div
      data-client-id=${id}
      data-testid="remote-presence-avatar"
      class="remote-selection-avatar-anchor"
      style=${styleMap({
        pointerEvents: 'none',
        transform: `translate(${x}px, ${y}px)`,
      })}
    >
      ${this.renderRemotePresenceAvatarVisual({ color, title, user })}
    </div>`;
  }

  private renderRemoteBlockPresenceAvatar(
    target: HTMLElement,
    { color, id, title, user }: RemoteBlockPresenceAvatar,
    rotate: number
  ) {
    renderLit(
      html`<div
        data-client-id=${id}
        data-testid="remote-presence-avatar"
        style=${styleMap({ pointerEvents: 'none' })}
      >
        ${this.renderRemotePresenceAvatarVisual({
          color,
          counterRotate: rotate,
          inline: true,
          title,
          user,
        })}
      </div>`,
      target
    );
  }

  /**
   * One pointer per assistant: while a person's assistant shows its own cursor,
   * the pointer of that person's agent changes steps back.
   */
  private _agentPointersWithoutCursor(): ReadonlyMap<string, AgentPointer> {
    const pointers = this.std.selection.agentPointers;
    const states = this.store.awarenessStore.getStates();
    const pointing = new Set<string>();
    for (const key of this.selection.remoteCursorSelectionMap.keys()) {
      if (!isAssistantPresenceKey(key)) continue;
      const owner = states.get(presenceOwnerClientId(key))?.user?.id;
      if (owner) pointing.add(owner);
    }
    if (pointing.size === 0) return pointers;
    return new Map(
      [...pointers].filter(
        ([, pointer]) =>
          pointer.agent.kind !== 'owner' || !pointing.has(pointer.ownerUserId)
      )
    );
  }

  override connectedCallback() {
    super.connectedCallback();

    const view = this.ownerDocument.defaultView!;
    // A database row whose change moved it is carried there by the pointer.
    const databaseReveal = new AgentDatabaseReveal({
      playback: this.std.selection.slots.agentPlayback,
      block: id => this.std.view.getBlock(id),
      view,
      reduced: () =>
        !isStreamLainMotionEnabled('canvas') ||
        view.matchMedia('(prefers-reduced-motion: reduce)').matches,
      drawn: () => agentPointers.update(),
      following: () => agentFollowing$.value,
    });
    this._disposables.add(() => databaseReveal.dispose());
    const agentPointers = new AgentPointerDisplay({
      pointers: () => this._agentPointersWithoutCursor(),
      carry: target => databaseReveal.carry(target),
      owner: createAgentPointerOwnerResolver(this.std),
      position: target => {
        const model = this.gfx.getElementById(target.id);
        // Only canvas elements have a place on the canvas.
        if (
          !(
            model instanceof GfxBlockElementModel ||
            model instanceof GfxPrimitiveElementModel
          )
        )
          return null;
        const block = this.std.view.getBlock(target.id);
        // A header control of the view, a database row or cell: where the
        // block's open view shows it; any other target stands at the
        // element's center.
        const shown = block
          ? agentPointerViewElement(block, target)
          : target.rowId
            ? null
            : undefined;
        if (shown === undefined) {
          const [x, y] = model.elementBound.center;
          return { x, y };
        }
        const rect = databaseReveal.placeOf(target, shown);
        if (!rect) return null;
        const [x, y] = this.gfx.viewport.toModelCoordFromClientCoord([
          rect.left + Math.min(12, rect.width / 2),
          rect.top + Math.min(12, rect.height / 2),
        ]);
        return { x, y };
      },
      scale: () => 1 / this.gfx.viewport.zoom,
      changed: () => this.requestUpdate(),
      view: this.ownerDocument.defaultView!,
      avatarCache: this.std.getOptional(UserAvatarCacheProvider) ?? null,
    });
    this._agentPointerDisplay = agentPointers;
    this._disposables.add(effect(agentPointers.update));
    this._disposables.add(() => {
      agentPointers.dispose();
      this._agentPointerDisplay = null;
    });

    const ghostOwners = new Map<string, string>();
    const reveal = new AgentCanvasReveal({
      playback: this.std.selection.slots.agentPlayback.pipe(
        tap(playback => {
          const active = new Set(this._agentGhosts.map(ghost => ghost.id));
          for (const id of ghostOwners.keys())
            if (!active.has(id)) ghostOwners.delete(id);
          for (const step of playback.steps)
            if (step.kind === 'delete')
              ghostOwners.set(step.target.id, playback.ownerUserId);
        })
      ),
      view: this.ownerDocument.defaultView!,
      model: id => this.gfx.getElementById(id) ?? null,
      place: (model, xywh) => this._placeTransientElement(model, xywh),
      zoom: () => this.gfx.viewport.zoom,
      reduced: () =>
        !isStreamLainMotionEnabled('canvas') ||
        this.ownerDocument.defaultView!.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches,
      ghosts: ghosts => {
        this._agentGhosts = ghosts.map(ghost => ({
          ...ghost,
          ownerUserId: ghostOwners.get(ghost.id)!,
        }));
      },
    });
    this._agentCanvasReveal = reveal;
    this._disposables.add(() => {
      reveal.dispose();
      this._agentCanvasReveal = null;
      ghostOwners.clear();
    });

    const { _disposables, store } = this;

    if (this.surface) {
      _disposables.add(
        this.surface.elementAdded.subscribe(this._updateOnElementChange)
      );
      _disposables.add(
        this.surface.elementRemoved.subscribe(this._updateOnElementChange)
      );
      _disposables.add(
        this.surface.elementUpdated.subscribe(this._updateOnElementChange)
      );
    }

    _disposables.add(
      store.slots.blockUpdated.subscribe(this._updateOnElementChange)
    );

    _disposables.add(
      this.selection.slots.remoteUpdated.subscribe(() => {
        this._remoteBlockSelectionBindingRetryCount = 0;
        this._updateRemoteRects();
      })
    );
    _disposables.add(
      this.selection.slots.remoteCursorUpdated.subscribe(() => {
        this._updateRemoteCursor();
        agentPointers.update();
      })
    );

    _disposables.add(
      this.gfx.viewport.viewportUpdated.subscribe(() => {
        this._updateTransform();
        agentPointers.update();
        if (
          [...this._remoteRects.values()].some(
            rect => rect.awaitsBlockStackingContext
          )
        ) {
          this._remoteBlockSelectionBindingRetryCount = 0;
          this._retryRemoteBlockSelectionBinding();
        }
      })
    );

    // CP-005: Re-render only when presentation visibility changes. Cursor
    // awareness updates are handled by _updateRemoteCursor; rendering on every
    // awareness change doubles work in large rooms.
    let visibleClientIdsSignature = this.getVisibleClientIdsSignature();
    let remoteAwarenessDisplaySignature =
      this.getRemoteAwarenessDisplaySignature();
    const awarenessHandler = () => {
      const nextSignature = this.getVisibleClientIdsSignature();
      const nextRemoteDisplaySignature =
        this.getRemoteAwarenessDisplaySignature();
      if (
        nextSignature !== visibleClientIdsSignature ||
        nextRemoteDisplaySignature !== remoteAwarenessDisplaySignature
      ) {
        visibleClientIdsSignature = nextSignature;
        remoteAwarenessDisplaySignature = nextRemoteDisplaySignature;
        this._updateRemoteCursor();
        this._updateRemoteRects();
        this.requestUpdate();
      }
    };
    this.store.awarenessStore.awareness.on('change', awarenessHandler);
    _disposables.add(() => {
      this.store.awarenessStore.awareness.off('change', awarenessHandler);
    });

    // sl-dda7/sl-i7kr: Clean up transient remote drag state on disconnect.
    _disposables.add(() => {
      this._clearRemoteDragPreviews();
      this._clearRemoteBlockSelectionDecorations();
      this._stopLerpLoop();
    });

    initializeRemoteBlockSelectionDisplay(
      () => new RemoteColorManager(this.std),
      colorManager => {
        this._remoteColorManager = colorManager;
      },
      this._updateTransform,
      this._updateRemoteRects
    );
  }

  override render() {
    const { _remoteRects, _remoteCursors, _remoteColorManager } = this;
    if (!_remoteColorManager) return nothing;

    // CP-005: Filter cursors based on presentation state
    const visibleClientIds = this.getVisibleClientIds();
    const shouldShowCursor = (clientId: number): boolean => {
      if (visibleClientIds === null) return true;
      return visibleClientIds.has(clientId);
    };
    const visibleRemoteRects = [..._remoteRects.entries()]
      .filter(([id]) => shouldShowCursor(id))
      .sort(([a], [b]) => a - b);
    const avatarStackCounts = new Map<string, number>();
    const selectedClientIds = new Set(visibleRemoteRects.map(([id]) => id));

    const rects = repeat(
      visibleRemoteRects,
      value => value[0],
      ([id, rect]) => {
        const color = _remoteColorManager.get(id) ?? '#1e88e5';
        const user =
          readPresenceUser(this.store.awarenessStore.getStates(), id) ??
          rect.user;
        const avatar = rect.usesBlockStackingContext
          ? nothing
          : (() => {
              const avatarKey = [
                Math.round(rect.left),
                Math.round(rect.top),
                Math.round(rect.width),
                Math.round(rect.height),
              ].join(':');
              const avatarIndex = avatarStackCounts.get(avatarKey) ?? 0;
              avatarStackCounts.set(avatarKey, avatarIndex + 1);
              return this.renderRemotePresenceAvatar({
                color,
                id,
                title: user?.name ?? 'Unknown',
                user,
                x:
                  rect.left +
                  rect.width -
                  REMOTE_SELECTION_AVATAR_SIZE +
                  REMOTE_SELECTION_AVATAR_OVERLAP -
                  avatarIndex * REMOTE_SELECTION_AVATAR_STACK_OFFSET,
                y:
                  rect.top -
                  REMOTE_SELECTION_AVATAR_SIZE +
                  REMOTE_SELECTION_AVATAR_OVERLAP,
              });
            })();

        return html`${rect.usesBlockStackingContext
          ? nothing
          : html`<div
              data-client-id=${id}
              data-testid="remote-selection-rect"
              class="remote-rect"
              style=${styleMap({
                pointerEvents: 'none',
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                borderStyle: rect.borderStyle,
                borderColor: color,
                transform: `translate(${rect.left}px, ${rect.top}px) rotate(${rect.rotate}deg)`,
              })}
            ></div>`}
        ${avatar}`;
      }
    );
    const ghosts = repeat(
      this._agentGhosts,
      ghost => ghost.id,
      ghost => {
        const bound = Bound.deserialize(ghost.xywh);
        const owner = [...this.store.awarenessStore.getStates()].find(
          ([, state]) => state.user?.id === ghost.ownerUserId
        );
        const color = owner ? _remoteColorManager.get(owner[0]) : '#1e88e5';
        return html`<div
          data-agent-delete-ghost=${ghost.id}
          class="remote-rect"
          style=${styleMap({
            pointerEvents: 'none',
            opacity: '0.5',
            borderStyle: 'dashed',
            borderColor: color,
            width: `${bound.w}px`,
            height: `${bound.h}px`,
            transform: `translate(${bound.x}px, ${bound.y}px)`,
          })}
        ></div>`;
      }
    );
    const fullscreenPresenceAvatars = repeat(
      this.getFullscreenPresenceAvatars(
        avatarStackCounts,
        selectedClientIds,
        shouldShowCursor
      ),
      avatar => avatar.id,
      avatar => this.renderRemotePresenceAvatar(avatar)
    );

    const cursors = repeat(
      [..._remoteCursors.entries()].filter(([id]) => shouldShowCursor(id)),
      value => value[0],
      ([id, cursor]) => {
        const color = _remoteColorManager.get(id);
        return html`<div
          data-client-id=${id}
          data-testid="remote-cursor"
          class="remote-cursor"
          style=${styleMap({
            pointerEvents: 'none',
            transform: `translate(${cursor.x}px, ${cursor.y}px) scale(calc(1/var(--v-zoom)))`,
            color,
          })}
        >
          ${MultiCursorDuotoneIcon({
            width: '24px',
            height: '24px',
            style: `fill: ${color}; stroke: ${color};`,
          })}
          <div
            class="remote-username"
            style=${styleMap({
              backgroundColor: color,
            })}
          >
            ${cursor.user?.name ?? 'Unknown'}
          </div>
        </div>`;
      }
    );

    return html`
      <div class="affine-edgeless-remote-selection">
        ${this._agentPointerDisplay?.render()}
        ${rects}${ghosts}${fullscreenPresenceAvatars}${cursors}
      </div>
    `;
  }

  @state()
  private accessor _remoteCursors: Map<
    number,
    {
      x: number;
      y: number;
      user?: UserInfo | undefined;
    }
  > = new Map();

  @state()
  private accessor _remoteRects: Map<
    number,
    {
      width: number;
      awaitsBlockStackingContext: boolean;
      height: number;
      borderStyle: string;
      left: number;
      top: number;
      rotate: number;
      usesBlockStackingContext: boolean;
      user?: UserInfo | undefined;
    }
  > = new Map();

  private readonly _remoteBlockSelectionViews = new Set<HTMLElement>();

  private _remoteBlockSelectionBindingRetryCount = 0;
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_EDGELESS_REMOTE_SELECTION_WIDGET]: EdgelessRemoteSelectionWidget;
  }
}
